import React, { createContext, useContext, useEffect, useState } from "react";
import { getPrepDetails, getSupplierById, getSuppliers } from "@/api/fulfillment";
import { getInventory, getInventoryAnalytics } from "@/api/inventory";
import { BuyCostType, BxErrors, FulfillmentType, GetPrepDetailsResponse, MskuPrepDetail, ProductCondition, ProductHazmat, ProductSalesRankItem } from "@boxem/core-api-schema";
import { Offer, getOffers, getSalesRanksByASINs } from "@/api/products";
import { useErrorModalContext } from "./ErrorModalContext";
import { logRocketError } from "@/utils/logUtils";


export interface SellerProductData {
  productId?: number;
  sku?: string;
  fnSku?: string;
  listPrice?: number;
  quantity?: number;
  condition?: ProductCondition;
  conditionNotes?: string;
  isFBM?: boolean;
  buyCost?: string; // @TODO this actually should be a number, that's what we get returned from the API
  buyCostType?: BuyCostType;
  status?: string;
  issues?: string;
  submittedAt?: string;
  name?: string;
  asin?: string;
  image?: string;
  expirationDate?: string;
  datePurchased?: string;
  supplierId?: number;
  shippingTemplateId?: number;
  strandedDate?: string;
  strandedRemoveDate?: string;
  strandedReason?: string;
  strandedAction?: string;
}

export type ProductPatch = {
  status?: string;
  issues?: string;
  sellerData?: SellerProductData[];
};

export class Product {
  // 'catalog' stuff (constant for all sellers):
  name?: string;
  asin?: string;
  image?: string;

  upc?: string;
  isbn?: string;
  gtin?: string;
  ean?: string;

  // these fluctuate, and aren't garunteed to be populated 
  // - they are fetched independently. 'category' is the category within the sales rank.
  category?: string;
  categoryLink?: string; // link to the amazon 'best sellers' page for this category.
  salesRank?: number;
  hazmat?: ProductHazmat[];

  // from a sql query ran at time of retrieval through the fbaBatchItem table relation, or null
  averageBuyCost?: number;

  buyCost?: string;

  buyCostType: BuyCostType;

  // inventory items will have this array populated with the various inventory-relevant parameters for the item.
  sellerData?: SellerProductData[];
  status?: string;
  issues?: string;
  submittedAt?: string;

  searchString?: string; // concatenation of 'asin', 'name', 'sellerData[*].sku' (stringified), all lower-case

  makeSearchString(): void {
    this.searchString = `${this.asin} ${this.name} ${this.upc} ${this.isbn} ${this.gtin} ${this.ean}`;  
    this.sellerData?.forEach(sd => this.searchString += ` ${sd.sku} ${sd.fnSku}`);
    this.searchString = this.searchString.toLowerCase();
  }
}

const InventoryContext = createContext(undefined);

interface InventoryContextData {
  isFetchingInventory: boolean;

  allSellerSkus: Set<string>;
  setAllSellerSkus: (skus: Set<string>) => void;

  inventoryFlat: Product[];
  inventory: Product[];
  setInventory: (inventory: Product[]) => void;
  fbmInventory: Product[];
  setFbmInventory: (inventory: Product[]) => void;
  addProductToInventory: (product: Product) => void;
  editProductInInventory: (sku: string, patch: ProductPatch) => void;

  getOffersForASIN:      (asin: string) => Promise<Offer[]>;
  getPrepDetailsForSKUs: (mskus: string[], skipSKUsWithCommas?: boolean) => Promise<[BxErrors | null, PrepDetails[]]>;
  clearPrepDetailsForSKUs: (mskus?: string[]) => void; // empties the cache for each SKU provided, or clears it entirely if parameter omitted
  getSalesRanksForASINs: (asins: string[]) => Promise<ProductSalesRankItem[]>;

  suppliers: Supplier[];
  setSuppliers: (suppliers: Supplier[]) => void;
  getSupplierBydId: (id: number) => Promise<Supplier>;

  isFetchingAnalytics: boolean;

  analytics: InventoryAnalytics;
  setAnalytics: (analytics: InventoryAnalytics) => void;
  refetchAnalytics: () => void;

  refetchWholeInventory: () => void;

  clearInventory: () => void;
}

export interface Supplier {
  id?: number;
  name: string;
}

export interface InventoryAnalyticsDataPoints {
  totalInStockQuantity: number;
  inStockBuyCost: number;
  inStockSalesValue: number;
}
export interface InventoryAnalytics {
  fba: InventoryAnalyticsDataPoints;
  fbm: InventoryAnalyticsDataPoints;
  totalSKUs: number;
  lastSynced?: number;
}

// a few different caches that persist until refresh/tab exit:

// keys are ASIN, offers are a list of prices offered on amazon for a given asin at the current time,
// with some additional metadata like 'is this the buy box', 'FBA/FBM', etc.
const cachedOffersMap: { [key: string]: Offer[] } = {};

// key is MSKU, prep details are things like 'is this item glass/fragile', etc.
export interface PrepDetails extends MskuPrepDetail {
  invalid?: boolean;
}
const cachedPrepDetails: {[key: string]: PrepDetails} = {};


// key is ASIN
const cachedSalesRanks: {[key: string]: ProductSalesRankItem} = {};

// This is needed for polling FBA/ FBM listings. When a polling call is made. We need to update the listing in the inventory with the polled listings status or issues.
// There is problem though. If the FBA/ FBM polling mechanisms are running in tandem. They will have different references to the inventoryFlat context var depending on when the polling was initiated.
// This happens because react state variables are stupid and will cause each mechanism to overwrite each others changes.
let globalInventoryFlat: Product[] = [];

export const useInventoryContext = () => useContext<InventoryContextData>(InventoryContext);

export const InventoryContextProvider = (props: { children: React.ReactNode }) => {
  const { handleErrorResponseGenerically } = useErrorModalContext();

  const [allSellerSkus, setAllSellerSkus] = useState<Set<string>>(new Set());
  const [inventory, setInventory] = useState<Product[]>([]);
  const [fbmInventory, setFbmInventory] = useState<Product[]>([]);
  const [inventoryFlat, setInventoryFlat] = useState<Product[]>([]); // same as 'inventory', but we don't merge ASINs together. used for display in the inventory table.
  const [isFetchingInventory, setIsFetchingInventory] = useState<boolean>(true);
  const [isFetchingAnalytics, setIsFetchingAnalytics] = useState<boolean>(false);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [analytics, setAnalytics] = useState<InventoryAnalytics>(null);

  // checks for local supplier, and if absent, makes a request to the api.
  const _getSupplierById = async (id: number): Promise<Supplier> => {
    const supplier = suppliers.find(supplier => supplier.id === id);
    if (!!supplier) return supplier;

    return getSupplierById(id)
      .then(supplier => {
        if (!!supplier) {
          setSuppliers([...suppliers, supplier]);
        }
        return supplier;
      });
  }

  const refetchSuppliers = async () => {
    try {
      setSuppliers(await getSuppliers());
    } catch (e) {
      console.error(e);
    }
  }

  const refetchAnalytics = async () => {
    setIsFetchingAnalytics(true);
    try {
      const analytics = await getInventoryAnalytics();
      setAnalytics(analytics);
    }
    catch (err) {
      console.error(err);
      setAnalytics(null);
    }
    finally {
      setIsFetchingAnalytics(false);
    }
  }

  const refetchWholeInventory = async () => {
    setIsFetchingInventory(true);

    // we get suppliers at the same time as inventory right now for simplicity's sake
    refetchSuppliers();

    try {
      const data = await getInventory();
      
      //This creates a flat array of the users inventory that is not grouped by asin.
      const invArray = data.map((inventoryItem) => {
        const product = new Product();

        product.sellerData = [
          {
            sku: inventoryItem.sku,
            fnSku: inventoryItem.fnSku,
            quantity: inventoryItem.quantity ?? 0,
            listPrice: inventoryItem.listPrice ?? 0,
            condition: inventoryItem.condition as ProductCondition,
            conditionNotes: inventoryItem.condition,
            productId: inventoryItem.productId,
            isFBM: inventoryItem.fulfillmentType === FulfillmentType.MFN,
            buyCost: inventoryItem.buyCost?.toString(), // @TODO remove we should just use a number...
            buyCostType: inventoryItem.buyCostType,
            status: inventoryItem.status,
            issues: inventoryItem.issues as string,
            submittedAt: inventoryItem.submittedAt,
            name: inventoryItem.name,
            image: inventoryItem.image0,
            asin: inventoryItem.asin,
            expirationDate: inventoryItem.expirationDate,
            datePurchased: inventoryItem.datePurchased,
            supplierId: inventoryItem.supplierId,
            shippingTemplateId: inventoryItem.shippingTemplateId,
            strandedDate: inventoryItem.strandedDate,
            strandedRemoveDate: inventoryItem.strandedRemoveDate,
            strandedReason: inventoryItem.strandedReason,
            strandedAction: inventoryItem.strandedAction
          }
        ];
        product.asin = inventoryItem.asin;
        product.upc = inventoryItem.upc;
        product.gtin = inventoryItem.gtin;
        product.isbn = inventoryItem.isbn;
        product.ean = inventoryItem.ean;
        product.image = inventoryItem.image0;
        product.name = inventoryItem.name;
        product.status = inventoryItem.status;
        product.issues = inventoryItem.issues as string;
        product.submittedAt = inventoryItem.submittedAt;
        product.averageBuyCost = inventoryItem.averageBuyCost ? parseFloat(inventoryItem.averageBuyCost) : 0;
        product.buyCost = inventoryItem.buyCost?.toString();  // @TODO remove we should just use a number...
        product.makeSearchString();

        return product;
      });

      setInventoryFlat([ ...invArray ]); //Used on the inventory page.
      globalInventoryFlat = invArray; //Used to keep track of a global reference to all listings while polling mechanisms are running.
    } catch (e) {

    } finally {
      setIsFetchingInventory(false);
    }
  };

  const addProductToInventory = (product: Product) => {
    //Add the new product in inventory flat.
    globalInventoryFlat = [ product, ...globalInventoryFlat ];
    setInventoryFlat([ ...globalInventoryFlat ]);
  };

  const editProductInInventory = (sku: string, patch: ProductPatch) => {
    //Patch the product in the FlatInventory with the new data.
    const flatProductIndex = globalInventoryFlat.findIndex(i => i.sellerData[0].sku === sku);
    if (flatProductIndex !== -1) {
      globalInventoryFlat[flatProductIndex].status = patch.status;
      globalInventoryFlat[flatProductIndex].issues = patch.issues;

      if (!!patch?.sellerData) {
        globalInventoryFlat[flatProductIndex].sellerData[0] = {
          ...globalInventoryFlat[flatProductIndex].sellerData[0],
          ...patch.sellerData[0],
        };
      }

      globalInventoryFlat[flatProductIndex].sellerData[0].status = patch.status;
      globalInventoryFlat[flatProductIndex].sellerData[0].issues = patch.issues;

      setInventoryFlat([ ...globalInventoryFlat ]);
    }
  };

  const clearInventory = () => {
    setAllSellerSkus(new Set());
    setInventory([]);
    setFbmInventory([]);
    setInventoryFlat([]);
    globalInventoryFlat = [];
    setSuppliers([]);
    setAnalytics(null);
  };

  useEffect(() => {

    //When an item in the inventory is added or modified we need to re-group the skus by asin so the listings in the search pages are properly reflected.
    //This isn't really necessary for FBM right now as we don't show the user all skus associated with an ASIN on the search page or allow them to replenish FBM listings from the listing flow.
    if (inventoryFlat.length > 0) {
      // group inventory items by ASIN, produce Set of all seller skus at the same time.
      const fbaInvMap: { [key: string]: Product } = {};
      const fbmInvMap: { [key: string]: Product } = {};
      const allSellerSkus = new Set<string>();

      //This will loop through the flat inventory array we jsut created and group skus together by ASIN.
      //It also sort the FBA/ FBM listings into their own arrays.
      for (let i = 0; i < inventoryFlat.length; i++) {
        const item = inventoryFlat[i];

        allSellerSkus.add(item.sellerData[0].sku); // don't forget to add it to the list of skus though!

        const productData: SellerProductData = {
          productId: item.sellerData[0].productId,
          sku: item.sellerData[0].sku,
          fnSku: item.sellerData[0].fnSku,
          quantity: item.sellerData[0].quantity ?? 0,
          condition: item.sellerData[0].condition,
          status: item.sellerData[0].status,
          issues: item.sellerData[0].issues,
        };

        if (item.sellerData[0].isFBM) {
          const entry = fbmInvMap[item.asin];
          if (entry) {
            const sellerDataIndex = entry.sellerData.findIndex(sd => sd.sku === item.sellerData[0].sku);
            const alreadySellerDataForSku = sellerDataIndex !== -1;

            if (!alreadySellerDataForSku) {
              entry.sellerData.push(productData);
              entry.makeSearchString();
            } else {
              entry.sellerData[sellerDataIndex] = {
                ...entry.sellerData[sellerDataIndex],
                ...productData,
              };
            }
          } else {
            fbmInvMap[item.asin] = item;
          }
        } else {
          const entry = fbaInvMap[item.asin];
          if (entry) {
            const sellerDataIndex = entry.sellerData.findIndex(sd => sd.sku === item.sellerData[0].sku);
            const alreadySellerDataForSku = sellerDataIndex !== -1;

            if (!alreadySellerDataForSku) {
              entry.sellerData.push(productData);
              entry.makeSearchString();
            } else {
              entry.sellerData[sellerDataIndex] = {
                ...entry.sellerData[sellerDataIndex],
                ...productData,
              };
            }
          } else {
            fbaInvMap[item.asin] = item;
          }
        }
      }

      setAllSellerSkus(allSellerSkus); //Just a list of sellerSkus

      // we still provide the inventory as an array. entries will be unique with respect to ASIN.
      // entries have an array of 'sellerData' which may contain multiple SKUs + other seller data.
      const fbaInv = Object.values(fbaInvMap);
      const fbmInv = Object.values(fbmInvMap);

      setInventory([ ...fbaInv ]); //Used on the FBA step 1 page.
      setFbmInventory([ ...fbmInv ]); //Used on the FBM step 1 page.
    }
  }, [inventoryFlat]);

  const getOffersForASIN = async (asin: string) => {
    const cachedOffers = cachedOffersMap[asin];
    if (!!cachedOffers) {
      return cachedOffers;
    
    } else if (!!asin) {
      return getOffers(asin)
        .then(offers => {
          cachedOffersMap[asin] = offers;
          return offers;
        })
        .catch(errors => {
          // @NOTE intentionally not showing error modal.
          // this endpoint can fail and it's ok - the item
          // just won't have offers shown. retries can be
          // done by closing the modal and re-opening it.
          // @TODO we can put some flag in the offers map that
          // tells us it failed to allow customers to more explicitly
          // re-request the offers.
        });
    }
    return [];
  }

  const clearPrepDetailsForSKUs = (mskus?: string[]) => { // empties the cache for each SKU provided, or clears it entirely if parameter omitted
    if (!mskus) {
      Object.keys(cachedPrepDetails).forEach(key => delete cachedPrepDetails[key]);
      return;
    }

    mskus.forEach(msku => delete cachedPrepDetails[msku]);
  }

  const getPrepDetailsForSKUs = async (mskus: string[], skipSKUsWithCommas: boolean = false): Promise<[BxErrors | null, PrepDetails[]]> => {
    const results: PrepDetails[] = [];

    let requestMskus: string[] = [];

    mskus.forEach(msku => {
      if (!cachedPrepDetails[msku]) {
        // https://developer-docs.amazon.com/sp-api/docs/url-encoding
        // the API requires the mskus to be passed as query params instead of in a request body,
        // which makes SKUs with commas in them unable to be disambiguated from a delimiting comma.
        // each SKU with a comma will have to be requested individually.

        // but this complicates garuntees about rate-limits (as well as performance/other reliability). 
        // If we happen to try to ask for 100 prepDetails,
        // where every SKU has a comma, we suddenly are making 100 requests instead of the single request we'd
        // need if there were no commas anywhere. So, we allow you to pass a flag that skips SKUs with commas,
        // so you can still request big batches and be reasonably confident it will work, but at a later time
        // you will have to call this function again for the individual SKUs with commas.
        
        // we currently do this in step 1 - after getting search results we ask to cache all of the skus
        // without commas. Then when you open a product in the add to shipment drawer we ask again for the sku
        // individually. If it didn't have a comma in it, it will be cached, otherwise, we'll make a request.
  
        if (skipSKUsWithCommas && msku.includes(",")) {
          console.info("Skipping retrieving prep details for sku because it has a comma in it: ", msku);
          return;

        } else {
          requestMskus.push(msku);
        }
      } else {
        results.push(cachedPrepDetails[msku]);
      }
    });

    if (requestMskus.length > 0) {
      let page = 0;
      const pageSize = 40;
      // in some circumstances, you can barely finish a short search (say, "Nike"),
      // have the results finalized for a millisecond, then initiate a new search (say, "Lego"),
      // and if your inventory is particularly large, you will potentially spawn, many, many,
      // many pages of requests. This can cause infra issues unfortunately, I've seen:
      //  1.  Error: read ECONNRESET
      //  2.  Error: sorry, too many clients already
      // and other errors.
      // so here we'll limit the number of parallel requests to 10. This is fine to discard
      // the results past the first 10, because it's a rare case they are needed, and in the case
      // they are, they will be requested at the moment the user opens the drawer for that item.
      const maxPages = 10;
      const numPages = Math.min(Math.ceil(requestMskus.length / pageSize), maxPages);
      const requests: Promise<GetPrepDetailsResponse>[] = [];
      while (page < numPages) {
        const slice = requestMskus.slice(page*pageSize, Math.min((page+1)*pageSize, requestMskus.length));
        requests.push(getPrepDetails(slice));
        page += 1;
      }
      try {
        const responses = await Promise.all(requests);
        responses.forEach(response => {
          // each response is a batch of size 1..pageSize
          response.mskuPrepDetails.forEach(mskuPrepDetail => {
            cachedPrepDetails[mskuPrepDetail.msku] = mskuPrepDetail;
            results.push(mskuPrepDetail);
          });
        })
      } catch (e: any) {
        return [e, []];
      }
    }

    return [null, results];
  }

  const getSalesRanksForASINs = async (asins: string[]): Promise<ProductSalesRankItem[]> => {
    const results: ProductSalesRankItem[] = [];
    const requestASINS: string[] = [];

    asins.forEach(asin => {
      const cachedSaleRank = cachedSalesRanks[asin];
      if (cachedSaleRank) {
        results.push(cachedSaleRank);
      } else {
        requestASINS.push(asin);
      }
    });

    if (requestASINS.length > 0) {
      try {
        const salesRanks = await getSalesRanksByASINs(requestASINS);
        salesRanks.forEach(salesRank => {
          cachedSalesRanks[salesRank.asin] = salesRank;
          results.push(salesRank);
        });
      } catch(e) {
        logRocketError(`Failed to retrieve sales ranks for asins: ${JSON.stringify(requestASINS)}, error: ${JSON.stringify(e)}`);
        // we'll gracefully return below, so even in error cases this function returns an empty array.
      }
    }

    return results;
  }

  return (
    <InventoryContext.Provider
      value={{
        isFetchingInventory,

        allSellerSkus,
        setAllSellerSkus,

        inventoryFlat,
        inventory,
        setInventory,
        fbmInventory,
        setFbmInventory,
        addProductToInventory,
        editProductInInventory,

        getOffersForASIN,
        getPrepDetailsForSKUs,
        clearPrepDetailsForSKUs,
        getSalesRanksForASINs,

        suppliers,
        setSuppliers,
        getSupplierBydId: _getSupplierById,

        isFetchingAnalytics,
        analytics,
        setAnalytics,
        refetchAnalytics,

        refetchWholeInventory,

        clearInventory,
      }}
    >
      {props.children}
    </InventoryContext.Provider>
  );
}
